import { StateEffect, StateField, type EditorState, type Extension } from "@codemirror/state";
import { EditorView, showTooltip, type Tooltip } from "@codemirror/view";

// Matches hex color literals like #RGB, #RRGGBB or #RRGGBBAA.
const hexColorRegex = /#(?:[0-9a-fA-F]{8}|[0-9a-fA-F]{6}|[0-9a-fA-F]{3})/g;

interface HexColorMatch {
  from: number;
  to: number;
  value: string;
}

/**
 * Finds the hex color literal that the primary cursor is currently positioned on,
 * if any. The cursor counts as "on" the color when it sits anywhere between the
 * start and end of the literal (inclusive).
 */
function findColorAtCursor(state: EditorState): HexColorMatch | null {
  const selection = state.selection.main;
  if (!selection.empty) return null;

  const pos = selection.head;
  const line = state.doc.lineAt(pos);
  const text = line.text;

  hexColorRegex.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = hexColorRegex.exec(text)) !== null) {
    const from = line.from + match.index;
    const to = from + match[0].length;
    if (pos >= from && pos <= to) {
      return { from, to, value: match[0] };
    }
  }

  return null;
}

/** Normalizes any supported hex form to the 6-digit `#rrggbb` that `<input type="color">` expects. */
function toInputColor(hex: string): string {
  let value = hex.replace("#", "");

  // Expand shorthand (#rgb -> #rrggbb).
  if (value.length === 3) {
    value = value
      .split("")
      .map((c) => c + c)
      .join("");
  }

  // Drop alpha channel if present (#rrggbbaa -> #rrggbb).
  if (value.length === 8) {
    value = value.slice(0, 6);
  }

  return `#${value.toLowerCase()}`;
}

// Tracks whether the editor field (or its picker) currently holds focus, so the
// tooltip can be hidden once the user leaves the field entirely.
const setFieldFocused = StateEffect.define<boolean>();

const fieldFocused = StateField.define<boolean>({
  create: () => false,
  update(value, tr) {
    for (const effect of tr.effects) {
      if (effect.is(setFieldFocused)) return effect.value;
    }
    return value;
  },
});

/** True when focus moved to the editor itself or into the color picker tooltip. */
function staysWithinField(view: EditorView, related: EventTarget | null): boolean {
  if (!(related instanceof Element)) return false;
  return view.dom.contains(related) || related.closest(".cm-color-picker-tooltip") !== null;
}

function createColorTooltip(match: HexColorMatch): Tooltip {
  return {
    pos: match.from,
    end: match.to,
    above: true,
    arrow: true,
    create: (view) => {
      const dom = document.createElement("div");
      dom.className = "cm-color-picker-tooltip";

      // Keep interactions with the picker from moving the editor selection,
      // which would otherwise recompute the tooltip and close the picker.
      dom.addEventListener("mousedown", (event) => event.stopPropagation());

      const input = document.createElement("input");
      input.type = "color";
      input.value = toInputColor(match.value);

      // Look up the current range on every change so edits stay correct even if
      // the document shifted since the tooltip was created.
      const applyColor = () => {
        const current = findColorAtCursor(view.state);
        if (current === null) return;

        // Preserve the casing style of the original literal (upper vs lower).
        const newHex = current.value === current.value.toUpperCase()
          ? input.value.toUpperCase()
          : input.value.toLowerCase();

        view.dispatch({
          changes: { from: current.from, to: current.to, insert: newHex },
        });
      };

      input.addEventListener("input", applyColor);

      // Close the tooltip when focus leaves the picker to somewhere outside the
      // editor field (e.g. after the native picker is dismissed).
      input.addEventListener("blur", (event) => {
        if (staysWithinField(view, event.relatedTarget)) return;
        view.dispatch({ effects: setFieldFocused.of(false) });
      });

      dom.appendChild(input);
      return { dom };
    },
  };
}

const colorPickerField = StateField.define<Tooltip | null>({
  create() {
    // Hidden until the editor field gains focus.
    return null;
  },
  update(value, tr) {
    if (!tr.state.field(fieldFocused)) return null;

    const focusChanged = tr.effects.some((effect) => effect.is(setFieldFocused));
    if (!tr.docChanged && !tr.selection && !focusChanged) return value;

    const match = findColorAtCursor(tr.state);
    if (match === null) return null;
    // Keep the existing tooltip (and its open picker) while the cursor stays on
    // the same literal; only rebuild when it moves to a different one.
    if (value && value.pos === match.from) return value;
    return createColorTooltip(match);
  },
  provide: (field) => showTooltip.from(field),
});

const focusHandlers = EditorView.domEventHandlers({
  focus(_event, view) {
    if (!view.state.field(fieldFocused)) {
      view.dispatch({ effects: setFieldFocused.of(true) });
    }
  },
  blur(event, view) {
    if (staysWithinField(view, event.relatedTarget)) return;
    view.dispatch({ effects: setFieldFocused.of(false) });
  },
});

const colorPickerTheme = EditorView.baseTheme({
  ".cm-color-picker-tooltip": {
    display: "flex",
    alignItems: "center",
    padding: "2px",
  },
  ".cm-color-picker-tooltip input[type=color]": {
    width: "1.75rem",
    height: "1.75rem",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    padding: "0",
  },
});

/**
 * CodeMirror extension that shows a native color picker tooltip whenever the
 * cursor is positioned on a hex color literal, updating the literal live.
 */
export function colorPickerTooltip(): Extension {
  return [fieldFocused, colorPickerField, focusHandlers, colorPickerTheme];
}
